import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  Package,
  DollarSign,
  Users,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  ShoppingCart,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Skeleton from '../../components/common/Skeleton';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Icon from '../../components/common/Icon';
import { useAuth } from '../../hooks/useAuth';
import { orderService } from '../../services/orderService';
import { dashboardService } from '../../services/dashboardService';
import { formatPrice } from '../../utils/priceCalculator';
import { formatOrderNumber } from '../../utils/statusBucket';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

const KPI = ({ icon, label, value, hint, color = 'primary' }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between gap-2 mb-2">
      <span className="text-sm text-app-text-secondary">{label}</span>
      <span className={`w-9 h-9 rounded-lg bg-${color}/10 text-${color} flex items-center justify-center`}>
        <Icon as={icon} size="md" />
      </span>
    </div>
    <div className="text-2xl font-extrabold text-app-text">{value}</div>
    {hint && <div className="text-xs text-app-text-secondary mt-1">{hint}</div>}
  </Card>
);

// Gráfico de barras minimalista — sem libs externas. Recebe series com
// `day` (string YYYY-MM-DD) e `revenue` (número). Renderiza barras
// proporcionais à altura máxima.
const RevenueChart = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.revenue));
  if (!series.length) {
    return <p className="text-sm text-app-text-secondary text-center py-8">Sem dados no período</p>;
  }
  return (
    <div className="flex items-end gap-1 h-32">
      {series.map((s) => {
        const h = Math.max(2, Math.round((s.revenue / max) * 100));
        return (
          <div key={s.day} className="flex-1 flex flex-col items-center gap-1" title={`${s.day}: ${formatPrice(s.revenue)}`}>
            <div
              className="w-full bg-primary rounded-t"
              style={{ height: `${h}%`, minHeight: '2px' }}
            />
            <span className="text-[10px] text-app-text-secondary">
              {s.day?.slice(5)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

function periodPreset(value) {
  const now = new Date();
  const to = now.toISOString();
  const days = parseInt(value, 10) || 30;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
  return { from, to };
}

const SupplierDashboard = () => {
  const { currentStore } = useAuth();
  const businessType = currentStore?.businessType || 'seller';
  const isCatalog = businessType === 'catalog';
  const isReseller = businessType === 'reseller';

  const [period, setPeriod] = useState('30');
  const [data, setData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const { from, to } = useMemo(() => periodPreset(period), [period]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      dashboardService.getStoreDashboard({ from, to }).catch(() => null),
      orderService.getAll({ limit: 5, page: 1 }).catch(() => null),
    ]).then(([dash, orders]) => {
      if (cancelled) return;
      setData(dash);
      setRecentOrders(orders?.rows || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [from, to]);

  const kpis = data?.kpis || {};

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Painel</h1>
          {currentStore && (
            <p className="text-sm text-app-text-secondary">
              {currentStore.name} ·{' '}
              <span className="uppercase text-xs tracking-wide">{businessType}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-app-text-secondary">Período:</span>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '7',  label: 'Últimos 7 dias' },
              { value: '30', label: 'Últimos 30 dias' },
              { value: '90', label: 'Últimos 90 dias' },
              { value: '180', label: 'Últimos 6 meses' },
              { value: '365', label: 'Último ano' }
            ]}
          />
        </div>
      </div>

      {/* 6 KPIs principais (Doc 5.1.1). */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="block" height={100} />)
        ) : (
          <>
            <KPI
              icon={ShoppingCart}
              label="Pedidos"
              value={kpis.totalOrders ?? 0}
              hint={`Pagos: ${kpis.paidOrders ?? 0}`}
            />
            <KPI
              icon={DollarSign}
              label="Faturamento"
              value={formatPrice(kpis.revenue || 0)}
              hint="Pedidos pagos"
            />
            <KPI
              icon={TrendingUp}
              label="Ticket médio"
              value={formatPrice(kpis.avgTicket || 0)}
            />
            <KPI
              icon={Package}
              label="Produtos vendidos"
              value={kpis.productsSold ?? 0}
            />
            <KPI
              icon={AlertTriangle}
              label="Estoque baixo"
              value={kpis.lowStockCount ?? 0}
              color="warning"
              hint={`≤ ${currentStore?.lowStockThreshold ?? 5} unid.`}
            />
            <KPI
              icon={Users}
              label={isReseller ? 'Revendedores ativos' : 'Total pedidos'}
              value={isReseller ? (kpis.activeResellers ?? 0) : (kpis.totalOrders ?? 0)}
            />
          </>
        )}
      </div>

      {/* Gráfico de receita */}
      {!isCatalog && (
        <Card className="p-5 mb-6">
          <h2 className="text-lg font-bold text-app-text mb-3">Faturamento diário</h2>
          {loading ? (
            <Skeleton variant="block" height={130} />
          ) : (
            <RevenueChart series={data?.series || []} />
          )}
        </Card>
      )}

      {/* Distribuição por bucket */}
      {!loading && data?.byBucket && (
        <Card className="p-5 mb-6">
          <h2 className="text-lg font-bold text-app-text mb-3">Pedidos por status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.byBucket).map(([k, v]) => (
              <div key={k} className="p-3 rounded-lg bg-app-surface-2 text-center">
                <p className="text-xs uppercase text-app-text-secondary">{k}</p>
                <p className="text-2xl font-bold text-app-text">{v}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Últimos pedidos */}
      {!isCatalog && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-app-text">Últimos pedidos</h2>
            <Link to="/fornecedor/painel/pedidos">
              <Button size="sm" variant="ghost" rightIcon={<ArrowRight size={14} />}>
                Ver todos
              </Button>
            </Link>
          </div>
          {loading ? (
            <Skeleton variant="text" lines={4} />
          ) : recentOrders.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title="Nenhum pedido ainda"
              description="Os pedidos aparecerão aqui assim que forem criados."
            />
          ) : (
            <ul className="divide-y divide-app-border">
              {recentOrders.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/fornecedor/painel/pedidos/${o.id}`}
                    className="flex items-center gap-3 py-3 hover:bg-app-surface-hover transition-colors rounded-md px-2 -mx-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-app-text">
                        {formatOrderNumber(o)}
                        {o.customer?.name ? ` · ${o.customer.name}` : ''}
                      </p>
                      <p className="text-xs text-app-text-secondary">
                        {formatRelativeTime(o.createdAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-app-text">
                        {formatPrice(o.totalToPay)}
                      </p>
                      <StatusBadge domain="order" status={o.statusBucket || o.status} size="sm" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};

export default SupplierDashboard;
